import React from "react";
import "../form.css";
import "./EditForm.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardList,
  faFloppyDisk,
  faTrash,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FIELD_OPTIONS, useEditForm } from "../../../hooks/useEditForm";

const EditForm = ({ show, onClose, form }) => {
  const {
    formTitle,
    setFormTitle,
    formDescription,
    setFormDescription,
    fields,
    addField,
    deleteField,
    updateField,
    updateCondition,
    optionInput,
    setOptionInput,
    handleSave,
  } = useEditForm(form, onClose);

  const handleOptionKeyPress = (e, field) => {
    if (e.key === "Enter" && optionInput[field.id]?.trim()) {
      e.preventDefault();
      const newOpt = optionInput[field.id].trim();
      const prevOpts = field.conditions?.options || [];
      updateCondition(field, "options", [...prevOpts, newOpt]);
      setOptionInput((prev) => ({ ...prev, [field.id]: "" }));
    }
  };

  const removeOption = (field, opt) => {
    const newOpts = (field.conditions?.options || []).filter((o) => o !== opt);
    updateCondition(field, "options", newOpts);
  };

  return (
    <div
      className={`offcanvas offcanvas-end ${show ? "show" : ""}`}
      style={{
        visibility: show ? "visible" : "hidden",
        backgroundColor: "white",
        zIndex: 1050,
      }}
      tabIndex="-1"
    >
      {/* HEADER */}
      <div className="offcanvas-header border-bottom">
        <FontAwesomeIcon
          icon={faClipboardList}
          className="me-3"
          style={{ color: "rgba(77, 100, 102, 1)", fontSize: "20px" }}
        />
        <h5 className="offcanvas-title">Modifier le formulaire</h5>
        <button type="button" className="btn-close" onClick={() => onClose(false)}></button>
      </div>

      {/* BODY */}
      <div className="offcanvas-body">
        {/* Title */}
        <div className="study-field py-1 px-4 w-100 mb-3">
          <span className="study-field-label">Titre du formulaire</span>
          <input
            type="text"
            className="w-100"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            placeholder="Saisir le titre de formulaire..."
          />
        </div>

        {/* Description */}
        <div className="study-field py-1 px-4 w-100 mb-3">
          <span className="study-field-label">Description</span>
          <textarea
            className="w-100"
            rows="4"
            placeholder="Décrire..."
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
          ></textarea>
        </div>

        <hr />

        {/* Fields */}
        <div className="overflow-y-auto px-0 m-0">
          <h6 className="ms-2 mb-4">Champs du formulaire</h6>

          {fields.map((field) => (
            <div className="row mb-3 w-100 border-bottom pb-3" key={field.id}>
              {/* Label */}
              <div className="col-12 mb-2">
                <div className="form-field py-1 px-4 d-flex flex-column position-relative">
                  <span className="form-field-label">Nom du variable</span>
                  <div className="position-relative w-100">
                    <input
                      type="text"
                      className="w-100 pe-5"
                      placeholder="Saisir..."
                      value={field.label}
                      onChange={(e) => updateField(field.id, "label", e.target.value)}
                    />
                    {fields.length > 1 && (
                      <FontAwesomeIcon
                        icon={faTrash}
                        onClick={() => deleteField(field.id)}
                        className="delete-icon"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Type */}
              <div className="col-8 mb-2">
                <div className="form-field py-1 px-4">
                  <span className="form-field-label">Type de variable</span>
                  <select
                    className="select-type w-100"
                    value={field.field_type}
                    onChange={(e) => updateField(field.id, "field_type", e.target.value)}
                  >
                    <option value="">-- Sélectionner --</option>
                    {FIELD_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Required */}
              {field.field_type !== "section-title" && (
                <div className="col-4 mb-2">
                  <div className="form-field py-1 px-4 d-flex flex-column">
                    <span className="form-field-label">Obligatoire</span>
                    <div className="d-flex gap-2 mt-1 align-items-center">
                      {["oui", "non"].map((opt) => (
                        <label
                          key={opt}
                          className="d-flex align-items-center"
                          style={{ fontSize: "0.85rem" }}
                        >
                          <input
                            type="radio"
                            name={`required-${field.id}`}
                            value={opt}
                            checked={
                              (opt === "oui" && field.required) ||
                              (opt === "non" && !field.required)
                            }
                            onChange={() =>
                              updateField(field.id, "required", opt === "oui")
                            }
                            className="me-1"
                            style={{ transform: "scale(0.9)" }}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ID Auto-generation */}
              {field.field_type === "id" && (
                <div className="col-12 mb-2">
                  <div className="form-field py-1 px-4">
                    <span className="form-field-label">
                      Générer automatiquement l'ID ?
                    </span>
                    <div className="d-flex gap-2 mt-1">
                      {["oui", "non"].map((opt) => (
                        <label key={opt} className="d-flex align-items-center">
                          <input
                            type="radio"
                            name={`auto-${field.id}`}
                            value={opt}
                            checked={
                              (opt === "oui" && field.conditions?.auto_generated) ||
                              (opt === "non" && !field.conditions?.auto_generated)
                            }
                            onChange={() =>
                              updateCondition(field, "auto_generated", opt === "oui")
                            }
                            className="me-1"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Checkbox & Radio options */}
              {(field.field_type === "checkbox" || field.field_type === "radio") && (
                <div className="col-12 mb-2">
                  <div className="form-field py-1 px-4">
                    <span className="form-field-label">Choix disponibles</span>
                    <div className="mb-2 d-flex flex-wrap gap-2">
                      {(field.conditions?.options || []).map((opt, i) => (
                        <div key={i} className="option-chip">
                          <span>{opt}</span>
                          <FontAwesomeIcon
                            icon={faTimes}
                            className="ms-2 remove-option"
                            onClick={() => removeOption(field, opt)}
                          />
                        </div>
                      ))}
                    </div>
                    <input
                      type="text"
                      className="w-100"
                      placeholder="Ajouter un choix et appuyer sur Entrée"
                      value={optionInput[field.id] || ""}
                      onChange={(e) =>
                        setOptionInput((prev) => ({
                          ...prev,
                          [field.id]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => handleOptionKeyPress(e, field)}
                    />
                  </div>
                </div>
              )}

              {/* Numeric or Date range */}
              {(field.field_type === "integer" ||
                field.field_type === "float" ||
                field.field_type === "date") && (
                <div className="col-12 mb-2">
                  <div className="form-field py-1 px-4">
                    <span className="form-field-label">Limites (min / max)</span>
                    <div className="d-flex gap-3 mt-1 align-items-center">
                      <div className="d-flex flex-column">
                        <label>Min</label>
                        <input
                          type={field.field_type === "date" ? "date" : "number"}
                          value={field.conditions?.min || ""}
                          onChange={(e) => updateCondition(field, "min", e.target.value)}
                        />
                      </div>
                      <div className="d-flex flex-column">
                        <label>Max</label>
                        <input
                          type={field.field_type === "date" ? "date" : "number"}
                          value={field.conditions?.max || ""}
                          onChange={(e) => updateCondition(field, "max", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add field button */}
        <div className="row mt-3">
          <div className="col-12 d-flex align-items-center justify-content-start">
            <button type="button" className="new-var" onClick={addField}>
              + variable
            </button>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="offcanvas-footer border-top p-3 bg-white">
        <button className="form-btn w-100" onClick={handleSave}>
          <FontAwesomeIcon icon={faFloppyDisk} className="me-2" />
          Enregistrer
        </button>
      </div>
    </div>
  );
};

export default EditForm;
